import React from "react";
import { ShippingReportUpdate } from "../../../types/shippingReports";
import { CarrierOption } from "../../../services/carriersApi";

type Props = {
  values: ShippingReportUpdate;
  onChange: (name: keyof ShippingReportUpdate, value: any) => void;
  disabled?: boolean;
  carrierOptions?: CarrierOption[];
  section?: "main" | "finish";
};

const inputClass =
  "mt-1 block min-w-0 max-w-full w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-slate-100";

const currentTorontoDateTime = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Toronto",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date());
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? "";
  return `${value("year")}-${value("month")}-${value("day")}T${value("hour")}:${value("minute")}`;
};

const formatRecordedTime = (value: string) =>
  new Intl.DateTimeFormat("en-CA", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "UTC",
  }).format(new Date(`${value}:00Z`));

function TextField({
  label,
  name,
  value,
  onChange,
  disabled,
  type = "text",
}: {
  label: string;
  name: keyof ShippingReportUpdate;
  value: string | null;
  onChange: Props["onChange"];
  disabled?: boolean;
  type?: string;
}) {
  return (
    <label className="block text-sm font-medium text-slate-700">
      {label} *
      <input
        type={type}
        step={type === "number" ? "0.01" : undefined}
        name={name}
        value={value ?? ""}
        disabled={disabled}
        onChange={(event) => onChange(name, event.target.value)}
        className={inputClass}
      />
    </label>
  );
}

function CarrierField({
  value,
  options,
  onChange,
  disabled,
}: {
  value: string | null;
  options: CarrierOption[];
  onChange: Props["onChange"];
  disabled?: boolean;
}) {
  const hasCurrentValue = options.some((option) => option.label === value);

  return (
    <label className="block text-sm font-medium text-slate-700">
      Carrier Name *
      <select
        value={value ?? ""}
        disabled={disabled}
        onChange={(event) => onChange("carrier_name", event.target.value)}
        className={inputClass}
      >
        <option value="">Select a carrier</option>
        {value && !hasCurrentValue ? <option value={value}>{value}</option> : null}
        {options.map((option) => (
          <option key={option.value} value={option.label}>{option.label}</option>
        ))}
      </select>
    </label>
  );
}

function DateTimeField({
  label,
  name,
  value,
  onChange,
  disabled,
}: {
  label: string;
  name: keyof ShippingReportUpdate;
  value: string | null;
  onChange: Props["onChange"];
  disabled?: boolean;
}) {
  return (
    <div className="min-w-0 max-w-full overflow-hidden">
      <div className="sm:hidden">
        <span className="block text-sm font-medium text-slate-700">{label} *</span>
        {value ? (
          <div className="mt-1 rounded-lg border border-indigo-200 bg-indigo-50 p-3">
            <div className="font-semibold text-indigo-950">
              {formatRecordedTime(value)}
            </div>
            <div className="mt-2 flex gap-3 text-xs">
              <button
                type="button"
                disabled={disabled}
                className="font-semibold text-indigo-700 disabled:text-slate-400"
                onClick={() => onChange(name, currentTorontoDateTime())}
              >
                Record current time again
              </button>
              <button
                type="button"
                disabled={disabled}
                className="font-medium text-slate-600 disabled:text-slate-400"
                onClick={() => onChange(name, null)}
              >
                Clear
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            disabled={disabled}
            className="mt-1 w-full rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 text-left text-sm font-semibold text-indigo-800 shadow-sm hover:bg-indigo-100 disabled:opacity-50"
            onClick={() => onChange(name, currentTorontoDateTime())}
          >
            Record {label}
          </button>
        )}
      </div>

      <div className="hidden sm:block">
        <label className="block text-sm font-medium text-slate-700">
          {label} *
          <input
            type="datetime-local"
            name={name}
            value={value ?? ""}
            disabled={disabled}
            onChange={(event) => onChange(name, event.target.value || null)}
            className={inputClass}
          />
        </label>
        <div className="mt-1 flex gap-3 text-xs">
          <button
            type="button"
            disabled={disabled}
            className="font-medium text-indigo-700 hover:text-indigo-900 disabled:text-slate-400"
            onClick={() => onChange(name, currentTorontoDateTime())}
          >
            Use current time
          </button>
          {value ? (
            <button
              type="button"
              disabled={disabled}
              className="font-medium text-slate-500 hover:text-slate-700"
              onClick={() => onChange(name, null)}
            >
              Clear
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function ShippingReportForm({
  values,
  onChange,
  disabled,
  carrierOptions = [],
  section = "main",
}: Props) {
  if (section === "finish") return (
    <div className="space-y-5">
      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <DateTimeField label="Truck Departure Time" name="departure_at" value={values.departure_at} onChange={onChange} disabled={disabled} />
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <DateTimeField label="Completion Time" name="completed_at" value={values.completed_at} onChange={onChange} disabled={disabled} />
      </section>
      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <label className="block text-sm font-medium text-slate-700">Notes *
          <textarea value={values.notes} disabled={disabled} rows={4} onChange={(event) => onChange("notes", event.target.value)} className={inputClass} />
        </label>
      </section>
    </div>
  );
  return (
    <div className="space-y-5">
      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <h2 className="text-base font-semibold text-slate-900">
          Shipment Information
        </h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <CarrierField value={values.carrier_name} options={carrierOptions} onChange={onChange} disabled={disabled} />
          <TextField label="Driver Name" name="driver_name" value={values.driver_name} onChange={onChange} disabled={disabled} />
          <TextField label="Trailer Number" name="trailer_number" value={values.trailer_number} onChange={onChange} disabled={disabled} />
          <TextField label="Trailer Plate Number" name="trailer_plate_number" value={values.trailer_plate_number} onChange={onChange} disabled={disabled} />
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-base font-semibold text-slate-900">
            Arrival and Loading
          </h2>
          <span className="text-xs font-medium text-slate-500">
            America/Toronto
          </span>
        </div>
        <div className="mt-4 grid min-w-0 gap-5 sm:grid-cols-2 lg:grid-cols-3 [&>*]:min-w-0">
          <DateTimeField label="Date & Time of Arrival" name="arrival_at" value={values.arrival_at} onChange={onChange} disabled={disabled} />
          <DateTimeField label="Start Time" name="started_at" value={values.started_at} onChange={onChange} disabled={disabled} />
        </div>
      </section>


    </div>
  );
}
